<script setup lang="ts">
import { useI18n } from '../../../i18n'
import type { DeckErrorDiagnostics } from '../../host/useDeckErrorDiagnostics'

/**
 * Why the deck could not be shown. For incompatible decks the missing cards can be
 * listed; with the `debug` attribute a report can be copied for support.
 */
const props = defineProps<{
  error: string
  diagnostics: DeckErrorDiagnostics
  debugEnabled: boolean
}>()

const { t } = useI18n()
const {
  copyState,
  copyFailureMessage,
  detailsOpen,
  missingCardsLoading,
  missingCards,
  missingCardIds,
  canShowMissingCards,
  copyDebugReport,
} = props.diagnostics
</script>

<template>
  <div class="status status--error">
    <div class="status__text">{{ error }}</div>

    <button
      v-if="canShowMissingCards"
      type="button"
      class="status__toggle"
      :aria-expanded="detailsOpen"
      :aria-label="t('deckPlayer.host.showMissingCards')"
      :title="detailsOpen ? t('deckPlayer.host.hideDetails') : t('deckPlayer.host.showDetails')"
      @click="detailsOpen = !detailsOpen"
    >
      <svg
        class="status__chevron"
        :class="{ 'status__chevron--open': detailsOpen }"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path fill="currentColor" d="M7 10l5 5 5-5H7z" />
      </svg>
    </button>

    <button
      v-if="debugEnabled"
      type="button"
      class="status__copy"
      :disabled="copyState === 'copying'"
      :title="
        copyState === 'copied' ? t('deckPlayer.host.copied') : t('deckPlayer.host.copyDebugInfo')
      "
      :aria-label="t('deckPlayer.host.copyDebugInfo')"
      @click="copyDebugReport"
    >
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path
          fill="currentColor"
          d="M16 1H6a2 2 0 0 0-2 2v12h2V3h10V1Zm3 4H10a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h9a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2Zm0 16H10V7h9v14Z"
        />
      </svg>
      <span class="status__copyText">
        {{ copyState === 'copied' ? t('deckPlayer.host.copied') : t('deckPlayer.host.copy') }}
      </span>
    </button>

    <div v-if="copyState === 'failed'" class="status__copyFail">
      {{ t('deckPlayer.host.copyFailed', { message: copyFailureMessage ?? '' }) }}
    </div>

    <div v-if="detailsOpen && canShowMissingCards" class="status__details">
      <div class="status__detailsHeader">
        {{ t('deckPlayer.host.missingCardsHeader', { count: missingCardIds.length }) }}
      </div>
      <div v-if="missingCardsLoading" class="status__detailsBody">{{ t('common.loading') }}</div>
      <ul v-else class="status__list">
        <li v-for="item in missingCards" :key="item.cardId" class="status__item">
          <span class="status__id">{{ item.cardId }}</span>
          <span class="status__sep">—</span>
          <span class="status__title">{{ item.title || t('deckPlayer.host.untitled') }}</span>
        </li>
        <li
          v-if="missingCards.length > 0 && missingCardIds.length > missingCards.length"
          class="status__more"
        >
          {{
            t('deckPlayer.host.titlesLoadedForFirst', {
              count: Math.min(missingCardIds.length, 30),
            })
          }}
        </li>
      </ul>
    </div>
  </div>
</template>

<style scoped>
.status {
  padding: 0.75rem 0.9rem;
  border-radius: 0.75rem;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  color: #0f172a;
}

.status--error {
  position: relative;
  border-color: #fecaca;
  background: #fef2f2;
  color: #991b1b;
  white-space: pre-wrap;
}

.status__text {
  padding-right: 3.25rem;
  padding-bottom: 2.75rem;
}

.status__copy {
  all: unset;
  position: absolute;
  right: 0.55rem;
  bottom: 0.55rem;
  height: 2rem;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  border-radius: 0.6rem;
  border: 1px solid rgba(153, 27, 27, 0.25);
  background: rgba(255, 255, 255, 0.65);
  color: rgba(153, 27, 27, 1);
  padding: 0 0.6rem;
  box-sizing: border-box;
  cursor: pointer;
}

.status__toggle {
  all: unset;
  position: absolute;
  right: 0.55rem;
  top: 0.55rem;
  width: 2rem;
  height: 2rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.6rem;
  border: 1px solid rgba(153, 27, 27, 0.25);
  background: rgba(255, 255, 255, 0.65);
  color: rgba(153, 27, 27, 1);
  padding: 0;
  box-sizing: border-box;
  cursor: pointer;
}

.status__toggle:hover {
  background: rgba(255, 255, 255, 0.85);
}

.status__chevron {
  width: 1.25rem;
  height: 1.25rem;
  transition: transform 140ms ease;
}

.status__chevron--open {
  transform: rotate(180deg);
}

.status__copy:hover {
  background: rgba(255, 255, 255, 0.85);
}

.status__copy:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.status__copy svg {
  width: 1.05rem;
  height: 1.05rem;
}

.status__copyText {
  font-weight: 650;
  font-size: 0.85rem;
}

.status__copyFail {
  margin-top: 0.5rem;
  font-size: 0.85rem;
  color: rgba(153, 27, 27, 0.85);
}

.status__details {
  margin-top: 0.75rem;
  border-top: 1px dashed rgba(153, 27, 27, 0.25);
  padding-top: 0.75rem;
}

.status__detailsHeader {
  font-weight: 700;
  color: rgba(153, 27, 27, 1);
  margin-bottom: 0.5rem;
}

.status__detailsBody {
  font-size: 0.9rem;
  color: rgba(153, 27, 27, 0.9);
}

.status__list {
  margin: 0;
  padding: 0;
  list-style: none;
  display: grid;
  gap: 0.25rem;
  max-height: 14rem;
  overflow: auto;
  padding-right: 0.25rem;
}

.status__item {
  display: grid;
  grid-template-columns: auto auto 1fr;
  gap: 0.5rem;
  align-items: baseline;
}

.status__id {
  font-family:
    ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New',
    monospace;
  font-size: 0.9rem;
}

.status__sep {
  opacity: 0.7;
}

.status__title {
  color: rgba(153, 27, 27, 0.92);
  font-size: 0.9rem;
}

.status__more {
  margin-top: 0.35rem;
  font-size: 0.85rem;
  opacity: 0.85;
}
</style>
